
import React, { useEffect } from "react";
import { BadgeDollarSign, ShieldCheck, PiggyBank, Clock, Calendar, CreditCard, FileText, DollarSign, FileSearch, Users } from "lucide-react";
import Footer from "@/components/Footer";
import Navbar from "@/components/Navbar";
import { ServiceSelectionProvider } from "@/contexts/ServiceSelectionContext";
import HeroSection from "@/components/product/HeroSection";
import VideoSection from "@/components/product/VideoSection";
import SimpleProcessSection from "@/components/product/SimpleProcessSection";
import BenefitsSection from "@/components/product/BenefitsSection";
import ComparisonSection from "@/components/product/ComparisonSection";
import ProductPlanSection from "@/components/product/ProductPlanSection";
import { CalculadoraAntecipacao } from "@/components/calculator/CalculadoraAntecipacao";
import SEO from "@/components/SEO";
import useMetaTracking from "@/hooks/use-meta-tracking";

const Antecipacao = () => {
  const { trackViewContent } = useMetaTracking();
  
  useEffect(() => {
    trackViewContent({
      content_name: "antecipacao",
      content_category: "product_page"
    });
  }, [trackViewContent]);

  const carouselBenefits = [
    {
      icon: <BadgeDollarSign className="w-12 h-12 text-drcash-caribbean-green" />,
      title: "Juros flexíveis",
      description: "Oferta de três modalidades - juros a partir de 1,11%, juros compartilhados entre clínica e paciente, ou sem juros para o paciente."
    },
    {
      icon: <ShieldCheck className="w-12 h-12 text-drcash-caribbean-green" />,
      title: "Sem entrada ou garantia",
      description: "Os pacientes não precisam pagar entrada nem oferecer bens em garantia para financiar procedimentos."
    },
    {
      icon: <PiggyBank className="w-12 h-12 text-drcash-caribbean-green" />,
      title: "Valor máximo aprovado",
      description: "Possibilidade de aprovação de valores altos, aumentando o ticket médio da clínica e permitindo oferecer mais produtos e serviços."
    },
    {
      icon: <Clock className="w-12 h-12 text-drcash-caribbean-green" />,
      title: "Recebimento em 48h",
      description: "A clínica recebe o valor rapidamente, bastando enviar a Nota Fiscal ou comprovante do procedimento."
    },
    {
      icon: <Calendar className="w-12 h-12 text-drcash-caribbean-green" />,
      title: "Carência de 30 dias",
      description: "O paciente tem 30 dias até o vencimento do primeiro boleto."
    },
    {
      icon: <CreditCard className="w-12 h-12 text-drcash-caribbean-green" />,
      title: "Boleto bancário como opção de pagamento",
      description: "Ampliação das formas de pagamento além de cartão de crédito, Pix ou plano de saúde."
    },
    {
      icon: <FileText className="w-12 h-12 text-drcash-caribbean-green" />,
      title: "Parcelamento flexível",
      description: "Opções de 6x a 24x, tornando o valor mensal mais acessível à renda familiar do paciente."
    },
    {
      icon: <DollarSign className="w-12 h-12 text-drcash-caribbean-green" />,
      title: "Valores altos",
      description: "Possibilidade de parcelar procedimentos de R$250 até R$30 mil."
    },
    {
      icon: <FileSearch className="w-12 h-12 text-drcash-caribbean-green" />,
      title: "Análise rápida",
      description: "Verificação digital que determina em segundos se o paciente está elegível ao financiamento."
    },
    {
      icon: <Users className="w-12 h-12 text-drcash-caribbean-green" />,
      title: "Gestão da inadimplência",
      description: "A cobrança e inadimplência são responsabilidade da Dr.Cash, permitindo que a clínica foque em suas atividades principais."
    }
  ];

  const antecipacaoSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": "Antecipação de Recebíveis Dr.Cash",
    "description": "Antecipe qualquer maquininha com uma taxa única de 1,9%. Receba no mesmo dia para solicitações até 15h.",
    "brand": {
      "@type": "Brand",
      "name": "Dr.Cash"
    },
    "offers": {
      "@type": "Offer",
      "priceCurrency": "BRL",
      "price": "1.9",
      "priceValidUntil": "2025-12-31",
      "itemCondition": "https://schema.org/NewCondition",
      "availability": "https://schema.org/InStock"
    }
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Como funciona a antecipação de recebíveis da Dr.Cash?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Na plataforma da Dr.Cash, você seleciona os valores e períodos que deseja antecipar em apenas 3 cliques. Para solicitações feitas até às 15h, o dinheiro é depositado no mesmo dia com taxa única de 1,9%."
        }
      },
      {
        "@type": "Question",
        "name": "É possível antecipar recebíveis de diferentes maquininhas?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Sim, nossa plataforma permite antecipar recebíveis de múltiplos CNPJs e diferentes adquirentes (Getnet, Stone, Cielo, PagSeguro, etc.) em uma única operação, tudo centralizado em um só dashboard."
        }
      }
    ]
  };

  return (
    <ServiceSelectionProvider>
      <SEO 
        title="Antecipação de Recebíveis para Clínicas | Taxa única de 1,9% | Dr.Cash"
        description="Antecipe recebíveis de qualquer maquininha com taxa única de 1,9%. Receba no mesmo dia, antecipe múltiplos CNPJs e maquininhas em um só lugar."
        keywords="antecipação recebíveis, antecipação cartão crédito, antecipação maquininhas, fluxo de caixa clínicas, recebimento mesmo dia"
        canonical="/antecipacao"
        structuredData={[antecipacaoSchema, faqSchema]}
      />
      <Navbar variant="dark" />
      <main>
        <HeroSection 
          title="Antecipe qualquer maquininha com uma taxa de 1,9%"
          description="Não espere 30, 60, 90 dias para receber, conosco você recebe no mesmo dia."
          ctaText="Comece agora"
          imageSrc="/lovable-uploads/3b8fcd1b-4453-4f91-b823-84ade19832f0.webp"
        />
        
        <VideoSection
          title="Veja como funciona na prática"
          videoEmbedId="/lovable-uploads/video2.mov"
        />

        <SimpleProcessSection />
        
        <CalculadoraAntecipacao />
        
        <BenefitsSection />
        
        <ComparisonSection />
        
        <ProductPlanSection
          title="Planos para todas as necessidades"
          subtitle="Selecione pelo menos um serviço e um plano para iniciar seu cadastro"
        />
      </main>
      <Footer />
    </ServiceSelectionProvider>
  );
};

export default Antecipacao;
